import { findResultPanel as api } from 'st-app/api'
import { createMemo, createSignal, createEffect, Accessor, Setter, untrack } from 'solid-js'
import { render } from "solid-js/web"
import { Show } from 'solid-js'
import STGrid, { STGridProps } from '../st-grid/st-grid';
import STToolButton from '../st-tool-button/st-tool-button'
import { ExposeInternals } from 'common';

import './st-find-result-panel.css';
import STModal from '../st-modal/st-modal'
import { ViewState } from 'st-app/api';
import { FindResultPanelContext, FindResultPanelContextJsx, requireFindResultPanelContext, useFindResultPanelContext } from './FindResultPanelContext';
import { SearchCriteriaList } from './Fragments/SearchCriteriaList';

export class FindResultPanelProps extends ExposeInternals {
    primaryObjectGU: string = '';

    visible? = true;
    pinned? = false;
}

const ST = (window as any).ST || {};
//const EMPTY_ROW = 'EMPTY_ROW';
const FIND_RESULTS_VIEW_GU = '967DB868-B68A-477D-87B8-313E598846FA';
const FIND_RESULTS_GRID_ID = 'FIND_RESULTS';

export default function FindResultPanel(props: FindResultPanelProps) {
    const context = useFindResultPanelContext() ?? new FindResultPanelContext();
    if (props.iface) {
        props.iface.context = context;
    }

    return <FindResultPanelContextJsx.Provider value={context}>
        <FindResultPanelInner {...props} />
    </FindResultPanelContextJsx.Provider>
}

function FindResultPanelInner(props: FindResultPanelProps) {
    const ctx = requireFindResultPanelContext();

    const [isVisible, setIsVisible] = createSignal(props.visible ?? true);
    createEffect(() => setIsVisible(props.visible ?? true));

    const [isPinned, setIsPinned] = createSignal(props.pinned ?? false);
    createEffect(() => setIsPinned(props.pinned ?? false));

    const [isCollapsed, setIsCollapsed] = createSignal(!props.pinned);
    createEffect(() => setIsCollapsed(!props.pinned));

    const gridProps = new STGridProps();
    gridProps.viewId = FIND_RESULTS_VIEW_GU;
    gridProps.gridId = FIND_RESULTS_GRID_ID;
    gridProps.events = ctx.events;
    gridProps.selectedRows = [ctx.selectedRows, ctx.setSelectedRows];

    createEffect(() => {
        ctx.setViewState({
            ...untrack(ctx.viewState),
            viewGU: FIND_RESULTS_VIEW_GU,
            serverState: {
                'PrimaryObjectGU': props.primaryObjectGU
            },
        });
    });

    let hoverTimer: any;
    function onMouseLeave() {
        clearTimeout(hoverTimer);
        if (!isPinned()) {
            setIsCollapsed(true);
        }
    }

    function onMouseEnter() {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
            setIsCollapsed(false);
        }, 250)
    }

    function toggleIsPinned() {
        setIsPinned(!isPinned());

        if (ctx.events?.onIsPinned) {
            ctx.events.onIsPinned(isPinned());
        }
    }

    if (ST.DEBUGGING_COMPONENT) {
        setTimeout(function () {
            setIsVisible(true);

            ctx.events.onRowClicked = function (ev) {
                console.log(this);
            }

            ctx.events.onDeleteSearchCriteria = function (srcName: string) {
                console.log('deleting');
            }
        }, 500);
    }

    async function openFindResults() {
        const res = await api.GetFindResults(ctx.viewState().viewGU || '', ctx.viewState().tabGU || '', ctx.viewState());

        if (ctx.events.onOpenFindResults) {
            ctx.events.onOpenFindResults(res);
        }
        else {
            console.log(res);
        }
    }

    let toggleVisibility = false;
    createEffect(function () {
        const pinned = isPinned();
        const visible = isVisible();

        if (visible && !pinned && !toggleVisibility) {
            toggleVisibility = true;
            setIsCollapsed(true);
            setIsVisible(false);
            requestAnimationFrame(() => {
                setIsVisible(true);
                toggleVisibility = false;
            });
        }
    })

    // When search criteria change, update the view data
    createEffect(() => {
        const search = ctx.searchCriteria();
        const viewState = untrack(ctx.viewState);

        viewState.dataElements = toElementList(search?.controls);
        viewState.gridElements = [];

        if (search?.grids) {
            for (const [key, value] of Object.entries(search.grids)) {
                const element = {
                    guid: key,
                    dataElements: toElementList(value.columns)
                };

                viewState.gridElements.push(element);
            }
        }
        ctx.setViewState({ ...viewState });

        function toElementList(controls: any) {
            const dataElements = [];

            if (controls) {
                for (const [key, value] of Object.entries(controls)) {
                    const element = {
                        srcName: key,
                        ...(value as any)
                    };

                    // If this has an fkSource, then that is what will provide the condition.
                    if (element.fkSource) {
                        element.value = '*';
                    }

                    dataElements.push(element);
                }
            }

            return dataElements;
        }
    });

    function returnToSearch() {
        if (ctx.events.onReturnToSearch) {
            ctx.events.onReturnToSearch(ctx.searchCriteria());
        }
    }

    const isGridVisible = createMemo(() => {
        const collapsed = isCollapsed();
        const { viewId, gridId } = gridProps;
        const viewState = ctx.viewState();

        return !!(!collapsed && viewId && gridId);
    });

    const hasSearchCriteria = createMemo(() => {
        const sc = ctx.searchCriteria() || {};

        return Object.keys(sc.controls || {}).length > 0
            || Object.keys(sc.grids || {}).length > 0;
    });

    return <Show when={isVisible()}>
        <Show when={ST.DEBUGGING_COMPONENT}>
            <div class="hidden debug auto" style="flex: 1 1 auto; background-color: lemonchiffon">&nbsp;</div>
        </Show>
        <div id="SearchResults" class="st-find-result-panel flexbox horizontal auto"
            onMouseLeave={onMouseLeave}
            classList={{ collapsed: !isPinned() && isCollapsed() }}>
            <div class="flexbox vertical panel auto" style="border: 1px solid var(--st-panel-border)">
                <h2 class="flexbox horizontal middle">
                    <span class="auto">Search Results</span>
                    <Show when={hasSearchCriteria()}>
                        <STToolButton icon={`fa-solid fa-arrow-right-to-arc spin-180`} label="Return To Search" onClick={returnToSearch} />
                    </Show>
                    <STToolButton icon="fa-solid fa-up-right-from-square" label="Open Find Results" onClick={() => openFindResults()} />
                    <Show when={isPinned()}>
                        <STToolButton icon="fa-solid fa-thumbtack" label="Unpin" onClick={toggleIsPinned} />
                    </Show>
                    <Show when={!isPinned()}>
                        <STToolButton icon="fa-thin fa-thumbtack" label="Pin" onClick={toggleIsPinned} />
                    </Show>
                </h2>
                <Show when={hasSearchCriteria()}>
                    <SearchCriteriaList searchCriteria={[ctx.searchCriteria, ctx.setSearchCriteria]} onDeleteSearchCriteria={ctx.events.onDeleteSearchCriteria} />
                </Show>
                <Show when={isGridVisible()}>
                    <STGrid {...gridProps} />
                </Show>
            </div>
            <div class="nav-bar flexbox vertical">
                <div role="button"
                    onMouseEnter={onMouseEnter}
                    onClick={() => setIsPinned(!isPinned())} style="min-height: 80px; cursor: pointer">
                    <span class="fas fa-magnifying-glass" role="presentation" aria-label="Toggle Search Panel"></span>
                </div>
                <div class="auto"></div>
            </div>
        </div>
    </Show>
}
FindResultPanel.defaultProps = new FindResultPanelProps();

